import { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useDebouncedCallback } from 'use-debounce';
import {
  createFileRoute,
  defaultStringifySearch,
  Outlet as ModalOutlet,
} from '@tanstack/react-router';
import logger from 'src/utils/logger';
import {
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Text,
  MenuTrigger,
  Menu,
  MenuItem,
} from 'react-aria-components';
import PageTitle from 'src/components/creator/PageTitle';
import MaterialsTable from 'src/components/creator/MaterialsTable';
import { PagingParams } from 'src/types/common';
import {
  PageLoader,
  Popover,
  Button,
  Paging,
  TextField,
  Select,
} from 'src/components/ui';
import IconSearch from 'src/assets/icons/search.svg';
import IconPdfFile from 'src/assets/icons/office-file-pdf-1.svg';
import IconMultimediaFile from 'src/assets/icons/audio.svg';
import MATERIALS_QUERY from 'src/graphql/query/materials.graphql';

const MaterialsPage = () => {
  const navigate = Route.useNavigate();
  const {
    tab = 'all',
    pageSize = 10,
    page = 1,
    q = '',
    format = 'pdf',
  } = Route.useSearch();

  const { loading } = useQuery(MATERIALS_QUERY, {
    variables: {
      filter: tab,
      limit: pageSize,
      format,
      offset: (page - 1) * pageSize,
      text: q,
    },
    fetchPolicy: 'cache-and-network',
  });

  const materials = useMemo(
    () => [
      {
        id: 999,
        title: 'Sample Material',
        isbn: '978-1-234-567-890',
        usedInCP: 1,
        created: '2021-01-01',
        published: '2021-01-01',
      },
      {
        id: 998,
        title: 'Sample Material 2',
        isbn: '978-1-234-567-891',
        usedInCP: 1,
        created: '2021-01-02',
        published: '2021-01-02',
      },
    ],
    []
  );

  const search = useSearchCallback();

  const debouncedSearch = useDebouncedCallback(
    (text: string) => search({ q: text }),
    500
  );

  const tabOptions = useTabOptions();

  return (
    <>
      <PageTitle title="Materials" />

      <div className="flex flex-col flex-1 bg-surface-alt rounded-xl shadow-3 mt-6 mb-2">
        <Tabs selectedKey={tab} className="flex flex-col flex-1 py-1">
          <TabList className="flex gap-4 px-4 border-b border-surface-alt-ghost">
            {tabOptions.map((option) => (
              <Tab
                key={option.id}
                id={option.id}
                href={defaultStringifySearch({ tab: option.id })}
                className={({ isSelected }) =>
                  `px-4 py-2 cursor-pointer ${
                    isSelected
                      ? 'border-b-2 border-secondary font-medium text-secondary'
                      : 'text-surface-alt-90'
                  }`
                }
              >
                {option.label}
              </Tab>
            ))}
          </TabList>

          {/* The tabs are links that swap this content through the `tab` search
              param, so there is one panel and its id tracks the selected key.
              Without a panel React Aria still points the selected tab's
              aria-controls at `<tabsId>-tabpanel-<key>`, which then resolves to
              nothing — an invalid ARIA reference (axe `aria-valid-attr-value`). */}
          <TabPanel id={tab} className="flex flex-col flex-1">
            <section className="flex items-center gap-2 px-4 py-3">
              <TextField
                name="search"
                size="small"
                placeholder="Search"
                defaultValue={q}
                Icon={IconSearch}
                className="w-full max-w-64"
                onChange={debouncedSearch}
                aria-label="Search materials"
              />

              <Select
                name="format"
                size="small"
                className="min-w-40"
                selectedKey={format.toString()}
                aria-label="Material format"
                items={[
                  { name: 'PDF', id: 'pdf' },
                  { name: 'Multimedia', id: 'multimedia' },
                ]}
                onSelectionChange={(key) => {
                  search({ format: key as 'pdf' | 'multimedia' });
                }}
              />

              <CreateButton className="px-5 ml-auto" />
            </section>

            <section className="h-full relative flex flex-col justify-between px-4 py-3">
              {loading && (
                <PageLoader className="absolute h-full w-full bg-transparent" />
              )}
              {materials.length > 0 && (
                <MaterialsTable
                  aria-label={`${tabOptions.find((t) => t.id === tab)?.label} Materials`}
                  className="mb-auto"
                  rows={materials}
                  onEdit={(material) =>
                    navigate({
                      to: `/materials/${material.id}/edit`,
                      search: (state) => state,
                    })
                  }
                  onDelete={(material) => {
                    logger.info('Delete material action', { id: material.id });
                  }}
                />
              )}
              {materials.length > 0 && (
                <Paging
                  totalItems={100} // todo: temp to test pagination
                  pageSize={pageSize}
                  page={page}
                  onPageSizeChange={(newPageSize) => {
                    void navigate({
                      search: (prev) => ({
                        ...prev,
                        pageSize: newPageSize,
                        page: 1,
                      }),
                    });
                  }}
                  onPageChange={(nextPage) => {
                    void navigate({
                      search: (prev) => ({ ...prev, page: nextPage }),
                    });
                  }}
                />
              )}
              {materials.length === 0 && !loading && (
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="w-full max-w-[31.25rem] text-surface-alt-90 text-center">
                    <Text className="font-bold text-2xl" elementType="h2">
                      Materials Library
                    </Text>
                    <Text
                      elementType="p"
                      className="font-medium text-base mt-4 mb-6"
                    >
                      Your materials library is empty. Add materials to use in
                      your course packs and make them available for students and
                      other creators.
                    </Text>

                    <CreateButton />
                  </div>
                </div>
              )}
            </section>
          </TabPanel>
        </Tabs>
      </div>

      {/* This outlet renders child modal routes (like create or edit material) */}
      <ModalOutlet />
    </>
  );
};

const CreateButton = ({ className }: { className?: string }) => {
  const navigate = Route.useNavigate();

  return (
    <MenuTrigger>
      <Button variant="success" size="small" className={className}>
        Create New
      </Button>
      <Popover
        placement="bottom right"
        className="p-1.5 w-full max-w-56 text-sm text-surface-alt-ghost-75 font-semibold select-none"
      >
        <Menu>
          <MenuItem
            className="flex items-center gap-2 cursor-pointer select-none py-2 pl-3 pr-1.5 rounded-lg outline outline-0 hover:bg-surface-alt-ghost"
            onAction={() =>
              navigate({
                to: '/materials/create/pdf',
                search: (state) => state,
              })
            }
          >
            <IconPdfFile
              aria-hidden="true"
              className="w-4 h-4 text-surface-ghost-45"
            />
            PDF Material
          </MenuItem>
          <MenuItem
            className="flex items-center gap-2 cursor-pointer select-none py-2 pl-3 pr-1.5 rounded-lg outline outline-0 hover:bg-surface-alt-ghost"
            onAction={() =>
              navigate({
                to: '/materials/create/multimedia',
                search: (state) => state,
              })
            }
          >
            <IconMultimediaFile
              aria-hidden="true"
              className="w-4 h-4 text-surface-ghost-45"
            />
            Multimedia Material
          </MenuItem>
        </Menu>
      </Popover>
    </MenuTrigger>
  );
};

const useTabOptions = () => {
  // Todo: hook to backend
  const userRole: string = 'creator+publisher';

  return useMemo(() => {
    const tabOptions = [{ id: 'all', label: 'All' }];

    switch (userRole) {
      case 'publisher': {
        tabOptions.push(
          { id: 'published', label: 'Published' },
          { id: 'lad-library', label: 'Lad Library' }
        );
        break;
      }
      case 'creator+publisher': {
        tabOptions.push(
          { id: 'private', label: 'Private' },
          { id: 'public', label: 'Public' },
          { id: 'published', label: 'Published' },
          { id: 'lad-library', label: 'Lad Library' }
        );
        break;
      }
      case 'creator':
      default: {
        tabOptions.push({ id: 'lad-library', label: 'Lad Library' });
        break;
      }
    }

    return tabOptions;
  }, [userRole]);
};

const useSearchCallback = () => {
  const navigate = Route.useNavigate();
  return useCallback(
    (newSearch: SearchParams) => {
      void navigate({
        search: (prev) => ({
          ...prev,
          ...newSearch,
        }),
      });
    },
    [navigate]
  );
};

interface SearchParams extends PagingParams {
  tab?: string;
  q?: string;
  format?: 'pdf' | 'multimedia';
}

export const Route = createFileRoute('/_authenticated/_creator/materials')({
  component: MaterialsPage,
  validateSearch: (search) => {
    return {
      tab: search.tab ?? 'all',
      pageSize: search.pageSize ?? 10,
      page: search.page ?? 1,
      q: search.q ?? '',
      format: search.format ?? 'pdf',
    } as SearchParams;
  },
});
